import { useEffect, useMemo, useState } from "react";
import api from "api/axiosInstance";
import Container from "../../layout/Container/Container";
import s from "./Technologies.module.scss";

type ClinicInfoRow = {
  id: number;
  name: string;
  detail: string;
};

type TechItem = {
  title: string;
  text: string;
};

const KEY_TECH_TITLE = "home.technologies.title";
const KEY_TECH_LEAD = "home.technologies.lead";
const KEY_TECH_ITEMS = "home.technologies.items";

// Текущие дефолты (как было в компоненте)
const DEFAULT_TITLE = "Оборудование и технологии";
const DEFAULT_LEAD =
  "Наш подход — сочетание опыта врачей и современных технологий, чтобы лечение было точным, понятным и спокойным.";

const DEFAULT_ITEMS: TechItem[] = [
  {
    title: "Лечение под микроскопом",
    text: "Кариес и корневые каналы лечим под увеличением — точнее и безопаснее.",
  },
  {
    title: "Фотопротокол",
    text: "Фиксируем этапы лечения фото — понятный контроль и прозрачность.",
  },
  {
    title: "3D‑сканирование",
    text: "Цифровые слепки — комфортнее и точнее традиционных.",
  },
  {
    title: "Профгигиена по протоколу",
    text: "Бережно и аккуратно подбираем режим под ваши зубы и дёсны.",
  },
  {
    title: "Цифровое планирование",
    text: "План лечения и результат — понятны заранее.",
  },
  {
    title: "Комфорт и спокойствие",
    text: "Без спешки: объясняем, показываем, делаем по шагам.",
  },
];

function safeParseItems(detail: string | undefined | null): TechItem[] | null {
  const raw = (detail ?? "").trim();
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;

    const items: TechItem[] = parsed
      .filter((x: any) => x && typeof x === "object")
      .map((x: any) => ({
        title: typeof x.title === "string" ? x.title.trim() : "",
        text: typeof x.text === "string" ? x.text.trim() : "",
      }))
      .filter((x) => x.title && x.text);

    return items.length ? items : null;
  } catch {
    return null;
  }
}

export default function Technologies() {
  const [rows, setRows] = useState<ClinicInfoRow[]>([]);
  const byKey = useMemo(() => {
    const m = new Map<string, ClinicInfoRow>();
    rows.forEach((r) => m.set(r.name, r));
    return m;
  }, [rows]);

  const [title, setTitle] = useState(DEFAULT_TITLE);
  const [lead, setLead] = useState(DEFAULT_LEAD);
  const [items, setItems] = useState<TechItem[]>(DEFAULT_ITEMS);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const res = await api.get<unknown>("/clinic-info");
        if (!alive) return;
        setRows(Array.isArray(res.data) ? (res.data as ClinicInfoRow[]) : []);
      } catch {
        // если clinic-info недоступен — остаёмся на дефолтах
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const t = byKey.get(KEY_TECH_TITLE)?.detail;
    const l = byKey.get(KEY_TECH_LEAD)?.detail;
    const it = byKey.get(KEY_TECH_ITEMS)?.detail;

    setTitle((t || "").trim() || DEFAULT_TITLE);
    setLead((l || "").trim() || DEFAULT_LEAD);

    const parsedItems = safeParseItems(it);
    setItems(parsedItems || DEFAULT_ITEMS);
  }, [byKey]);

  return (
    <div className={s.section}>
      <Container>
        <div className={s.panel}>
          <div className={s.headRow}>
            <h2 className={s.title}>{title}</h2>
            <p className={s.lead}>{lead}</p>
          </div>

          <div className={s.grid}>
            {items.map((it) => (
              <article key={it.title} className={s.card}>
                <h3 className={s.cardTitle}>{it.title}</h3>
                <p className={s.cardText}>{it.text}</p>
              </article>
            ))}
          </div>
        </div>
      </Container>
    </div>
  );
}
